import { useState } from 'react';
import { toast } from 'sonner';
import { useClientMutations } from '@/api/hooks';
import type { ClientOrg } from '@/api/schemasMembers';
import { FormField, fieldA11y } from '@/components/FormField';
import { Kicker } from '@/components/Kicker';
import { ProblemAlert } from '@/components/ProblemAlert';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { SUSPEND_EFFECTS, SUSPEND_REASON_MAX, validateSuspendReason } from './clientLifecycleModel';

interface DialogProps {
  /** The client to act on; `null` closes the dialog. */
  org: ClientOrg | null;
  onDone: () => void;
}

/** Suspend a client with a required reason; the dialog states what suspending does (SPEC §17.6). */
export function SuspendClientDialog({ org, onDone }: DialogProps) {
  return (
    <AlertDialog open={Boolean(org)} onOpenChange={(o) => (o ? undefined : onDone())}>
      <AlertDialogContent data-testid="client-suspend-dialog">{org ? <SuspendBody org={org} onDone={onDone} /> : null}</AlertDialogContent>
    </AlertDialog>
  );
}

function SuspendBody({ org, onDone }: { org: ClientOrg; onDone: () => void }) {
  const { suspend } = useClientMutations(org.id);
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const error = touched ? validateSuspendReason(reason) : undefined;
  const confirm = async () => {
    setTouched(true);
    if (validateSuspendReason(reason)) return;
    try {
      await suspend.mutateAsync(reason.trim());
      toast.success(`${org.name} suspended`, { description: 'Its people, tokens and builds are refused from their next request.' });
      onDone();
    } catch { /* shown in the dialog */ }
  };
  return (
    <>
      <AlertDialogHeader>
        <AlertDialogTitle className="break-words">Suspend {org.name}?</AlertDialogTitle>
        <AlertDialogDescription>Use it when a client must stop using Hangar now: unpaid, offboarding, or a security concern.</AlertDialogDescription>
      </AlertDialogHeader>
      <Effects />
      <FormField id="client-suspend-reason" label="Reason" required error={error} hint="Operators read it on the client page; the client never sees it">
        <Textarea data-testid="client-suspend-reason" rows={3} maxLength={SUSPEND_REASON_MAX} value={reason} onChange={(e) => setReason(e.target.value)} {...fieldA11y('client-suspend-reason', error, 'hint')} />
      </FormField>
      <ProblemAlert error={suspend.error} testId="client-suspend-error" />
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="client-suspend-dismiss">Keep it active</AlertDialogCancel>
        <AlertDialogAction destructive data-testid="client-suspend-confirm" disabled={suspend.isPending} onClick={(e) => { e.preventDefault(); void confirm(); }}>
          {suspend.isPending ? 'Suspending…' : 'Suspend client'}
        </AlertDialogAction>
      </AlertDialogFooter>
    </>
  );
}

function Effects() {
  return (
    <div className="rounded-lg border bg-background/60 px-3.5 py-3">
      <Kicker className="text-[11px] text-subtle">What happens</Kicker>
      <ul className="mt-2 flex flex-col gap-1.5 text-[13px] text-muted-foreground" aria-label="What happens">
        {SUSPEND_EFFECTS.map((line) => (
          <li key={line} className="flex items-start gap-2">
            <span aria-hidden className="mt-[7px] size-1.5 shrink-0 rounded-full bg-awaiting" />
            <span className="min-w-0">{line}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Reactivate a suspended client: its people and tokens work again; canceled builds stay canceled. */
export function ReactivateClientDialog({ org, onDone }: DialogProps) {
  return (
    <AlertDialog open={Boolean(org)} onOpenChange={(o) => (o ? undefined : onDone())}>
      <AlertDialogContent data-testid="client-reactivate-dialog">{org ? <ReactivateBody org={org} onDone={onDone} /> : null}</AlertDialogContent>
    </AlertDialog>
  );
}

function ReactivateBody({ org, onDone }: { org: ClientOrg; onDone: () => void }) {
  const { reactivate } = useClientMutations(org.id);
  const confirm = async () => {
    try {
      await reactivate.mutateAsync();
      toast.success(`${org.name} reactivated`, { description: 'Its people, API and CI tokens work again from their next request.' });
      onDone();
    } catch { /* shown in the dialog */ }
  };
  return (
    <>
      <AlertDialogHeader>
        <AlertDialogTitle className="break-words">Reactivate {org.name}?</AlertDialogTitle>
        <AlertDialogDescription>Its people sign in again and its API and CI tokens work again. Builds canceled by the suspension stay canceled: run them again if needed.</AlertDialogDescription>
      </AlertDialogHeader>
      <ProblemAlert error={reactivate.error} testId="client-reactivate-error" />
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="client-reactivate-dismiss">Keep it suspended</AlertDialogCancel>
        <AlertDialogAction data-testid="client-reactivate-confirm" disabled={reactivate.isPending} onClick={(e) => { e.preventDefault(); void confirm(); }}>
          {reactivate.isPending ? 'Reactivating…' : 'Reactivate client'}
        </AlertDialogAction>
      </AlertDialogFooter>
    </>
  );
}
